import { LoadingOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, RouteMap, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Spin } from 'antd'
import React, { useEffect, useMemo, useRef } from 'react'

import type { InvoiceTransaction } from '../../../../../api/useFreight/types'
import { useOrderRouteLog } from '../../../../../api/useFreight/useOrderRouteLog'
import { useOrders } from '../../../../../api/useOrders'
import { RouteLogTimeline } from '../../../OrderFreightDetails/components/RouteLogTimeline'
import { InvoiceDropSummary } from '../../../OrderFreightDetails/components/InvoiceDropSummary'
import { MapboxMap } from '@/components/_SiteWide/MapboxMap'
import { useMapboxMap } from '@/components/_SiteWide/MapboxMap/hooks/useMapboxMap'
import { PointsOfInterestLayer } from '@/components/OrderDispatch/DriverTracking/components/DriverMap/components/Layers/PointsOfInterest'
import { OrderRouteLayer } from '@/components/OrderDispatch/DriverTracking/components/DriverMap/components/Layers/OrderRouteLayer'
import { LidCodeIcon } from '@/components/Freight/OrderFreightDetails/components/LidCodeIcon'
import { Link } from 'react-router-dom'
import { useUserSettings } from '@/hooks/settings'
import { useDriverTracking } from '@/components/OrderDispatch/DriverTracking/hooks/useDriverTracking'
import {
  getInitialInvoiceBounds,
  getInvoiceActionPins,
  getInvoiceBreadcrumbRoute,
  getInvoicePointsOfInterest,
} from '../utils/calculations'
import { actions } from '@/components/OrderDispatch/DriverTracking/stores'
import {
  DEFAULT_VIEW_STATE,
  MAP_IMAGES,
} from '@/components/OrderDispatch/DriverTracking/components/DriverMap/constants'
import { MapPopover } from '@/components/OrderDispatch/DriverTracking/components/DriverMap/components/MapPopover'
import { MapStyleToggle } from '@/components/OrderDispatch/DriverTracking/components/DriverMap/components/MapStyleToggle'
import { IconMailShare, IconMapPinShare } from '@tabler/icons-react'
import { useSecurityContext } from '@/components/Navigation/SecurityContext'
import { getRouteStops } from '@/components/Freight/OrderFreightDetails/utils/routeUtils'
import { NoRouteData } from '@/components/OrderDispatch/DriverTracking/components/DriverMap/components/NoRouteData'

interface SingleOrderDisplayProps {
  order: InvoiceTransaction
  isLoading?: boolean
}

export function SingleOrderDisplay({ order, isLoading = false }: SingleOrderDisplayProps) {
  const { useOrderRouteLogQuery } = useOrderRouteLog()
  const { updateSetting } = useUserSettings()
  const { scopes } = useSecurityContext()

  const mapboxMapHook = useMapboxMap('invoices')
  const { mapRef, popoverInfo, flyToLocation, handleMapStyleChange, mapSettings, setIsStyleLoaded } = mapboxMapHook
  const mapZoomRef = useRef<boolean>(false)

  const { pointsOfInterestQuery, driverSearchQuery, setActiveQueryFilters } = useDriverTracking({
    disablePolling: true,
  })
  const {
    data: { data: driverSearchData } = { data: [] },
  } = driverSearchQuery
  const {
    data: { data: pointsOfInterest } = { data: [] },
  } = pointsOfInterestQuery

  /** Reset map state and refetch query data */
  useEffect(() => {
    mapZoomRef.current = false
    setActiveQueryFilters({ order_numbers: [order.order_number] })
  }, [order.order_number, setActiveQueryFilters])

  /** Initialize breadcrumb data on map and zoom */
  useEffect(() => {
    if (driverSearchData.length === 0 || !mapRef?.current) return

    actions.initializeOrdersOnRoute(driverSearchData[0])
    actions.setSelectedDriverId(driverSearchData[0]?.driver_id)

    if (!mapRef?.current) return
    const bounds = getInitialInvoiceBounds(driverSearchData, order.order_number)
    if (bounds) {
      mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 15 })
      mapZoomRef.current = true
    }
  }, [driverSearchData, mapRef?.current])

  const mapLayerData = useMemo(() => {
    return {
      orderRoute: driverSearchData?.flatMap((d) =>
        d.route.filter((r) => r.order_number === order.order_number && r.load_status === 'loaded')
      ),
      breadcrumbRoute: getInvoiceBreadcrumbRoute(driverSearchData, order.order_number),
      actionPins: getInvoiceActionPins(driverSearchData, order.order_number),
      pointsOfInterestOnRoute: getInvoicePointsOfInterest(driverSearchData, order.order_number, pointsOfInterest),
      driverNames: driverSearchData?.map((d) => d.driver_name).join(', '),
    }
  }, [driverSearchData, order.order_number, pointsOfInterest])

  const { data: routeLogData, isLoading: isRouteLogLoading } = useOrderRouteLogQuery(order.order_id)
  const projectedRouteStops = useMemo(() => getRouteStops(routeLogData), [routeLogData])

  if (isLoading || isRouteLogLoading) {
    return (
      <Vertical flex={3} horizontalCenter verticalCenter>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </Vertical>
    )
  }

  return (
    <Vertical border='all' borderRadius={8}>
      <Horizontal className='p-2' style={{ backgroundColor: 'var(--gray-100)' }}>
        <Texto category='p2' className='px-2'>
          Order Number - {order.order_number}
        </Texto>
      </Horizontal>
      <Horizontal flex={1}>
        <Vertical height={'auto'} flex={2.5}>
          <Horizontal className='py-2 px-3' gap={8} verticalCenter justifyContent='space-between' border='bottom'>
            <Horizontal gap={6} verticalCenter>
              <Texto appearance='light'>Transported by</Texto>
              <Texto>{order.carrier_name || mapLayerData.driverNames || 'Not Assigned'}</Texto>
            </Horizontal>
            <Horizontal gap={6} verticalCenter>
              <Texto appearance='light'>Trailer</Texto>
              <Texto>{routeLogData?.trailer_info?.trailer_config || 'N/A'}</Texto>
            </Horizontal>
            {routeLogData?.trailer_info?.compartments?.length && (
              <Horizontal borderRadius={2} gap={6} verticalCenter>
                <Texto appearance='light'>Compartments</Texto>
                {routeLogData?.trailer_info?.compartments?.map((compartment) => (
                  <LidCodeIcon
                    key={`${compartment.product_name}-${compartment.compartment_index}`}
                    productName={compartment.product_name || ''}
                    lidUrl={compartment.lid_code_url || ''}
                  />
                ))}
              </Horizontal>
            )}
            <Link
              target='_blank'
              to={`/order_movements_page/report?${new URLSearchParams({ order_number: order.order_number.toString() })}`}
              onClick={() => {
                localStorage.setItem('current-order-movements-report-view', 'all')
                updateSetting('current-order-movements-report-view', 'all')
              }}
            >
              <GraviButton
                appearance='filled'
                size='small'
                icon={<IconMailShare style={{ verticalAlign: 'middle' }} size={16} />}
                buttonText='Order Movements'
              />
            </Link>
            {scopes?.driver_tracking?.view && (
              <Link
                target='_blank'
                to={`/order_dispatch/driver_tracking?${new URLSearchParams({ order_number: order.order_number.toString() })}`}
              >
                <GraviButton
                  appearance='filled'
                  size='small'
                  icon={<IconMapPinShare size={16} style={{ verticalAlign: 'middle' }} />}
                  buttonText='Driver Tracking'
                />
              </Link>
            )}
          </Horizontal>
          {scopes?.scopes?.includes('d.t') ? (
            <MapboxMap
              ref={mapRef}
              mapStyle={mapboxMapHook.gravitateMapStyleURL}
              mapImages={MAP_IMAGES}
              initialViewState={DEFAULT_VIEW_STATE}
              setIsStyleLoaded={setIsStyleLoaded}
            >
              <PointsOfInterestLayer
                mapboxMapHook={mapboxMapHook}
                pointsOfInterest={mapLayerData.pointsOfInterestOnRoute}
                hoveredId={popoverInfo?.id}
              />
              <OrderRouteLayer
                mapboxMapHook={mapboxMapHook}
                key={order.order_number}
                id={String(order.order_number)}
                breadcrumbRoute={mapLayerData.breadcrumbRoute}
                actionPins={mapLayerData.actionPins}
                showBreadcrumbs={true}
              />
              {!mapLayerData?.orderRoute?.length && <NoRouteData />}
              <MapPopover popoverInfo={popoverInfo} mapRef={mapRef} />
              <MapStyleToggle currentStyle={mapSettings?.style || 'default'} handleMapStyle={handleMapStyleChange} />
            </MapboxMap>
          ) : (
            <Vertical>
              <RouteMap
                stops={projectedRouteStops}
                options={{
                  styles: [
                    {
                      featureType: 'poi',
                      elementType: 'labels',
                      stylers: [{ visibility: 'off' }],
                    },
                    {
                      featureType: 'poi.business',
                      stylers: [{ visibility: 'off' }],
                    },
                    {
                      featureType: 'transit',
                      elementType: 'labels.icon',
                      stylers: [{ visibility: 'off' }],
                    },
                  ],
                }}
              />
            </Vertical>
          )}
        </Vertical>

        {/* Right Content */}
        <Vertical flex={1} gap={8} border='left'>
          <RouteLogTimeline
            orderRoute={mapLayerData.orderRoute}
            timezone={driverSearchData?.[0]?.timezone}
            flyToLocation={flyToLocation}
          />
          {routeLogData && <InvoiceDropSummary routeLogData={routeLogData} />}
        </Vertical>
      </Horizontal>
    </Vertical>
  )
}
